import { Link } from "@tanstack/react-router";
import type { BreadcrumbItem } from "@/components/layout/HeaderContext";
import { cn } from "./cn";

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Truncate text to a maximum length with ellipsis.
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + "\u2026"; // \u2026 is ellipsis
}

/**
 * Breadcrumb navigation component.
 *
 * Requirements:
 * - Slash-separated segments with consistent spacing
 * - Last segment NOT clickable (just text)
 * - Previous segments are clickable links with underline on hover
 * - Truncate each segment to 50 chars with ellipsis if needed
 * - Single segment (e.g., "All clubs") renders as non-clickable text
 */
export function Breadcrumb({ items, className }: BreadcrumbProps) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={cn("flex items-center gap-2", className)}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const truncatedLabel = truncateText(item.label, 50);

        return (
          <span key={index} className="flex items-center gap-2">
            {/* Separator (slash) - not before first item */}
            {index > 0 && (
              <span
                className="text-body-1 text-text-tertiary select-none"
                aria-hidden="true"
              >
                /
              </span>
            )}

            {/* Breadcrumb item */}
            {isLast || !item.path ? (
              // Last segment or no path: non-clickable text
              <span
                className="text-body-1 text-text-primary"
                title={item.label.length > 50 ? item.label : undefined}
              >
                {truncatedLabel}
              </span>
            ) : (
              // Clickable segment
              <Link
                to={item.path}
                className="text-body-1 text-text-secondary hover:text-text-primary hover:underline transition-colors"
                title={item.label.length > 50 ? item.label : undefined}
              >
                {truncatedLabel}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
